import {
  factoryService,
  getJsonRequest,
  patchJsonRequest,
  postJsonRequest,
  putJsonRequest
} from '@chilecompra/react-kit/requests'
import {
  REACT_APP_API_BASE_URL,
  REACT_APP_API_MOCK_CCS,
  REACT_APP_API_MOCK_SEND_EMAIL
} from '../config/settings/environment'

/**
 * Gets the identity by the given DNI.
 */
export const getIdentity = factoryService(({ dni }) =>
  getJsonRequest(
    `${REACT_APP_API_BASE_URL}/v1/usuario-registro/informacionPersona/${dni}?origen=inscripcion-proveedor-front&validaFallecido=true`
  )
)

/**
 * Validates the existence of the user in the Mercado Público.
 */
export const validateUser = factoryService(({ dni }) =>
  getJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario/${dni}?tipo=1`)
)

/**
 * Verify that dni and document number are valid in the Civil Registry.
 */
export const validateDni = factoryService(({ dni, documentNumber }) => {
  const body = {
    rut: dni,
    numeroSerie: documentNumber
  }

  return postJsonRequest(`${REACT_APP_API_BASE_URL}/v1/registro-civil/cedula`, { body })
})

/**
 * Save a new temporary user and send an email to verify its existence,
 * then the backend save it in the final tables.
 */
export const sendRegistrationEmail = factoryService(({ guest, email, isForeign, isUniquePass }) => {
  const { country, name, firstLastName, gender, phone, phoneCode, secondLastName } = guest
  const identificationNumber = isForeign ? guest.identificationNumber : guest.dni
  const query = REACT_APP_API_MOCK_SEND_EMAIL === 'true' ? '?email=false' : ''

  const body = {
    email,
    apellidoPaterno: firstLastName,
    apellidoMaterno: secondLastName,
    esClaveUnica: isUniquePass,
    esExtranjero: isForeign ? 2 : 1,
    idGenero: gender,
    idPais: country,
    nombre: name,
    numeroIdentificacion: identificationNumber,
    telefono: `${phoneCode}-${phone}`
  }

  return postJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal${query}`, { body, retries: 2 })
})

/**
 * Create a temporary seller.
 */
export const createTemporarySeller = factoryService(
  ({
    address,
    conformation,
    country,
    district,
    dni,
    guestID,
    incomeBracket,
    incomeYear,
    isForeign,
    name,
    nickname,
    size,
    state,
    type,
    isPublicOrg
  }) => {
    const query = REACT_APP_API_MOCK_CCS === 'true' ? '?ccs=false' : ''
    const body = {
      anoTramo: incomeYear,
      conformacionEmpresa: conformation || undefined,
      direccionEmpresa: address,
      esExtranjero: isForeign ? 2 : 1,
      idComuna: district || undefined,
      idPais: country || undefined,
      idPersona: guestID,
      idRegion: state || undefined,
      idTramo: incomeBracket || null,
      nombreFantasia: nickname,
      razonSocial: name,
      rutEmpresa: dni || undefined,
      tipoEmpresa: type || undefined,
      tipoTamanoEmpresa: size || undefined,
      esOrgPublico: isPublicOrg
    }

    return postJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal/empresa${query}`, { body })
  }
)

/**
 * Create the temporary seller areas.
 */
export const createTemporarySellerAreas = factoryService(({ areas, businessID }) => {
  const body = areas.map(area => ({
    idEmpresa: businessID,
    idRubroEmpresa: area.value,
    nomRubroEmpresa: area.name
  }))

  return postJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal/empresaRubro`, { body })
})

/**
 * Create the seller.
 */
export const createSeller = factoryService(({ businessID }) => {
  const query = REACT_APP_API_MOCK_CCS === 'true' ? '?ccs=false' : ''
  const body = { idEmpresa: businessID }

  return postJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/registro${query}`, {
    body,
    retries: 3,
    retryBehavior: '5XX_ERROR'
  })
})

/**
 *
 * Mark user how to recover admin.
 */
export const recoverAdmin = factoryService(({ id }) => {
  const body = { id }
  return putJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal/recuperar-admin`, { body, retries: 2 })
})

/**
 *
 * Resend a new email.
 */
export const resendRegistrationEmail = factoryService(({ id }) => {
  const body = { id }
  return putJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal/reenviarCorreo`, { body, retries: 2 })
})

/**
 * Update the user information.
 */
export const updateRegistrationInformation = factoryService(({ id, email }) => {
  const query = REACT_APP_API_MOCK_SEND_EMAIL === 'true' ? '?email=false' : ''
  const body = { id, email }

  return patchJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal${query}`, { body })
})

/**
 * Update the user password.
 */
export const updatePassword = factoryService(({ id, password }) => {
  const query = REACT_APP_API_MOCK_SEND_EMAIL === 'true' ? '?email=false' : ''
  const body = { id, contrasena: password }

  return patchJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal${query}`, { body })
})

/**
 * Validate the code sent to the user's email.
 */
export const codeValidation = factoryService(({ id, code }) => {
  const body = { idPersona: id, codigo: code }
  return postJsonRequest(`${REACT_APP_API_BASE_URL}/v1/usuario-temporal/verificacion`, { body })
})

/**
 *
 * Associate temporary user with company already created
 */
export const associateAdmin = factoryService(({ dniUser, dniBusiness, id, isUniquePass, isTaxesKey }) => {
  const body = {
    rutPersona: dniUser,
    rutEmpresa: dniBusiness,
    esClaveUnica: isUniquePass,
    esClaveTributaria: isTaxesKey,
    idUsuario: id
  }
  return postJsonRequest(`${REACT_APP_API_BASE_URL}/v1/proveedor/asociar-admin`, { body })
})
